import React from "react";
import { Link } from "react-router-dom";
import { 
  ChevronRight, 
  Home, 
  AlertTriangle,
  ArrowRight,
  Phone,
  Search,
  Heart,
  Brain,
  Users,
  Shield,
  Activity,
  Clock,
  Zap,
  AlertCircle
} from "lucide-react";
import { Header, Footer } from "../../../components/Layout";

const CocainaPage = () => {
  const symptoms = [
    { title: "Euforia intensa pero breve", description: "Sensación de energía y bienestar que dura minutos, seguida de depresión" },
    { title: "Necesidad de consumir más", description: "Tolerancia rápida que lleva a aumentar dosis y frecuencia" },
    { title: "Paranoia y ansiedad", description: "Desconfianza extrema, sensación de ser perseguido o vigilado" },
    { title: "Cambios físicos visibles", description: "Pérdida de peso, deterioro dental, problemas nasales (cocaína)" },
    { title: "Ciclos de consumo intenso", description: "Períodos de uso compulsivo seguidos de agotamiento extremo" },
    { title: "Deterioro rápido", description: "La pasta base especialmente causa daño físico y mental acelerado" }
  ];

  const differences = [
    {
      substance: "Cocaína (clorhidrato)",
      description: "Polvo blanco que se inhala. Efecto de 30-60 minutos. Costo más alto.",
      effects: ["Euforia moderada", "Mayor control inicial", "Daño nasal progresivo"],
      risk: "Alto"
    },
    {
      substance: "Pasta Base (PBC)",
      description: "Forma impura que se fuma. Efecto de 5-10 minutos. Extremadamente adictiva.",
      effects: ["Euforia intensa y breve", "Adicción casi inmediata", "Deterioro físico rápido"],
      risk: "Extremo"
    },
    {
      substance: "Crack",
      description: "Cristales que se fuman. Similar a pasta base pero procesado diferente.",
      effects: ["Efecto inmediato", "Alta peligrosidad", "Daño pulmonar severo"],
      risk: "Extremo"
    }
  ];

  const healthEffects = [
    { system: "Cardiovascular", effects: ["Infartos", "Arritmias", "Hipertensión severa"], severity: "crítico" },
    { system: "Neurológico", effects: ["Convulsiones", "Derrames cerebrales", "Psicosis"], severity: "crítico" },
    { system: "Respiratorio", effects: ["Daño pulmonar", "Hemorragias nasales", "Neumonía"], severity: "alto" },
    { system: "Psicológico", effects: ["Paranoia", "Depresión severa", "Ideación suicida"], severity: "crítico" }
  ];

  const treatmentOptions = [
    {
      title: "Internación Inmediata",
      description: "Para usuarios de pasta base, la internación urgente es crítica. El craving es tan intenso que el tratamiento ambulatorio rara vez funciona.",
      urgency: "crítica",
      icon: Shield
    },
    {
      title: "Desintoxicación Supervisada",
      description: "Manejo del síndrome de abstinencia con apoyo médico y psicológico 24/7.",
      urgency: "alta",
      icon: Activity
    },
    {
      title: "Terapia Cognitivo-Conductual",
      description: "Trabajo intensivo en triggers, manejo del craving y prevención de recaídas.",
      urgency: "esencial",
      icon: Brain
    },
    {
      title: "Tratamiento Prolongado",
      description: "La adicción a estimulantes requiere tratamientos de 6-12 meses mínimo, más 6-12 meses de seguimiento posterior.",
      urgency: "necesaria",
      icon: Clock
    }
  ];

  const pageSchema = {
    "@context": "https://schema.org",
    "@type": "MedicalCondition",
    "name": "Adicción a Cocaína y Pasta Base",
    "alternateName": ["Adicción a estimulantes", "Trastorno por uso de cocaína"],
    "description": "La adicción a cocaína y pasta base es una de las más destructivas, caracterizada por dependencia rápida y deterioro físico acelerado.",
    "signOrSymptom": symptoms.map(s => ({
      "@type": "MedicalSignOrSymptom",
      "name": s.title
    }))
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Inicio", "item": "https://sinadicciones.org" },
      { "@type": "ListItem", "position": 2, "name": "Adicciones", "item": "https://sinadicciones.org/adicciones" },
      { "@type": "ListItem", "position": 3, "name": "Cocaína y Pasta Base", "item": "https://sinadicciones.org/adicciones/cocaina" }
    ]
  };

  return (
    <div className="min-h-screen bg-slate-950">
      
        <title>Adicción a Cocaína y Pasta Base: Síntomas y Tratamiento | Sin Adicciones</title>
        <meta name="description" content="Información sobre adicción a cocaína y pasta base en Chile. Diferencias, síntomas, efectos en la salud y opciones de tratamiento urgente." />
        <meta property="og:title" content="Cocaína y Pasta Base: Síntomas y Tratamiento" />
        <meta property="og:description" content="Guía completa sobre adicción a estimulantes: cocaína, pasta base y crack. Tratamiento y recuperación en Chile." />
        <link rel="canonical" href="https://sinadicciones.org/adicciones/cocaina" />
        <meta property="og:image" content="https://sinadicciones.org/og-image.png" />
        <meta property="og:image:width" content="1280" />
        <meta property="og:image:height" content="896" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://sinadicciones.org/adicciones/cocaina" />
        <meta property="og:site_name" content="SinAdicciones.org" />
        <meta property="og:locale" content="es_CL" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://sinadicciones.org/og-image.png" />

        <script type="application/ld+json">{JSON.stringify(pageSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      

      <Header />
      <div className="h-16 md:h-20" />

      {/* Breadcrumb */}
      <div className="bg-slate-900 border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <nav className="flex items-center gap-2 text-sm text-slate-400">
            <Link to="/" className="hover:text-emerald-400 transition-colors flex items-center gap-1">
              <Home className="w-4 h-4" />
              Inicio
            </Link>
            <ChevronRight className="w-4 h-4" />
            <Link to="/adicciones" className="hover:text-emerald-400 transition-colors">
              Adicciones
            </Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-slate-300">Cocaína y Pasta Base</span>
          </nav>
        </div>
      </div>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-red-900/20 via-slate-900 to-slate-950" />
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-72 h-72 bg-red-500/10 rounded-full blur-3xl" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 py-16 md:py-20">
          <div className="max-w-4xl">
            <div className="flex items-center gap-3 mb-6">
              <span className="text-5xl">⚡</span>
              <div className="px-4 py-2 bg-red-500/10 border border-red-500/20 rounded-full">
                <span className="text-red-400 font-medium">Alta peligrosidad</span>
              </div>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              Cocaína y Pasta Base:{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-400 to-orange-400">
                Adicción a Estimulantes
              </span>
            </h1>
            
            <p className="text-lg md:text-xl text-slate-300 mb-8 max-w-3xl">
              La pasta base es una de las drogas más destructivas en Chile. 
              La adicción se desarrolla rápidamente y el deterioro físico y mental es acelerado. 
              El tratamiento urgente puede salvar vidas.
            </p>
            
            <div className="flex flex-wrap gap-4">
              <Link 
                to="/centros-de-rehabilitacion-drogas-chile"
                className="px-6 py-3 bg-red-500 hover:bg-red-600 text-white font-semibold rounded-xl transition-all flex items-center gap-2"
              >
                <Search className="w-5 h-5" />
                Buscar Tratamiento Urgente
              </Link>
              <a 
                href="tel:1412"
                className="px-6 py-3 bg-slate-800 hover:bg-slate-700 text-white font-semibold rounded-xl transition-all border border-slate-700 flex items-center gap-2"
              >
                <Phone className="w-5 h-5" />
                Fono Drogas: 1412
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Warning Banner */}
      <section className="bg-red-500/10 border-y border-red-500/20 py-6">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center gap-4">
            <AlertCircle className="w-8 h-8 text-red-400 flex-shrink-0" />
            <div>
              <h3 className="text-red-400 font-bold">Advertencia Importante</h3>
              <p className="text-slate-300">
                La pasta base genera adicción severa desde las primeras dosis. Si tú o un familiar está consumiendo, 
                busca ayuda profesional de inmediato. El tratamiento ambulatorio generalmente no es efectivo para esta adicción.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Differences Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Tipos de Estimulantes
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Aunque relacionadas, estas sustancias tienen diferencias importantes en potencia, peligrosidad y efectos.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {differences.map((item, index) => (
              <div 
                key={index}
                className={`p-6 rounded-2xl border ${
                  item.risk === 'Extremo' 
                    ? 'bg-red-500/10 border-red-500/30' 
                    : 'bg-orange-500/10 border-orange-500/30'
                }`}
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-bold text-white">{item.substance}</h3>
                  <span className={`text-xs px-3 py-1 rounded-full font-medium ${
                    item.risk === 'Extremo' 
                      ? 'bg-red-500/20 text-red-400' 
                      : 'bg-orange-500/20 text-orange-400'
                  }`}>
                    Riesgo {item.risk}
                  </span>
                </div>
                
                <p className="text-slate-400 text-sm mb-4">{item.description}</p>
                
                <ul className="space-y-2">
                  {item.effects.map((effect, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-slate-300 text-sm">
                      <Zap className={`w-4 h-4 ${item.risk === 'Extremo' ? 'text-red-400' : 'text-orange-400'}`} />
                      {effect}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Symptoms */}
      <section className="py-16 bg-slate-900/50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Señales de Adicción
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              La adicción a estimulantes progresa rápidamente. Estas son las señales más comunes.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {symptoms.map((symptom, index) => (
              <div 
                key={index}
                className="p-6 bg-slate-800/50 rounded-2xl border border-red-500/20"
              >
                <div className="flex items-start gap-4">
                  <AlertTriangle className="w-6 h-6 text-red-400 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-lg font-bold text-white mb-2">{symptom.title}</h3>
                    <p className="text-slate-400 text-sm">{symptom.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Health Effects */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Efectos en la Salud
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Los estimulantes causan daño severo a múltiples sistemas del cuerpo. Muchos efectos pueden ser mortales.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {healthEffects.map((item, index) => (
              <div 
                key={index}
                className={`p-6 rounded-2xl border ${
                  item.severity === 'crítico' 
                    ? 'bg-red-500/10 border-red-500/30' 
                    : 'bg-orange-500/10 border-orange-500/30'
                }`}
              >
                <div className="flex items-center gap-2 mb-4">
                  <Brain className={`w-6 h-6 ${item.severity === 'crítico' ? 'text-red-400' : 'text-orange-400'}`} />
                  <h3 className="text-xl font-bold text-white">{item.system}</h3>
                  <span className={`ml-auto text-xs px-2 py-1 rounded-full ${
                    item.severity === 'crítico' 
                      ? 'bg-red-500/20 text-red-400' 
                      : 'bg-orange-500/20 text-orange-400'
                  }`}>
                    {item.severity}
                  </span>
                </div>
                
                <div className="flex flex-wrap gap-2">
                  {item.effects.map((effect, idx) => (
                    <span 
                      key={idx}
                      className="px-3 py-1 bg-slate-700/50 text-slate-300 text-sm rounded-full"
                    >
                      {effect}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Treatment */}
      <section className="py-16 bg-slate-900/50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Tratamiento Recomendado
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              La adicción a cocaína y especialmente a pasta base requiere tratamiento intensivo y prolongado.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {treatmentOptions.map((option, index) => (
              <div 
                key={index}
                className="p-6 bg-slate-800/50 rounded-2xl border border-slate-700/50"
              >
                <div className="flex items-start gap-4">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
                    option.urgency === 'crítica' 
                      ? 'bg-red-500/20' 
                      : 'bg-emerald-500/20'
                  }`}>
                    <option.icon className={`w-6 h-6 ${
                      option.urgency === 'crítica' ? 'text-red-400' : 'text-emerald-400'
                    }`} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="text-xl font-bold text-white">{option.title}</h3>
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        option.urgency === 'crítica' 
                          ? 'bg-red-500/20 text-red-400' 
                          : option.urgency === 'alta'
                          ? 'bg-orange-500/20 text-orange-400'
                          : 'bg-emerald-500/20 text-emerald-400'
                      }`}>
                        {option.urgency}
                      </span>
                    </div>
                    <p className="text-slate-400">{option.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 text-center">
            <Link 
              to="/centros-de-rehabilitacion-drogas-chile"
              className="inline-flex items-center gap-2 px-8 py-4 bg-red-500 hover:bg-red-600 text-white font-bold rounded-xl transition-all"
            >
              <Search className="w-5 h-5" />
              Buscar Centros con Internación
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4">
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-emerald-900/50 to-slate-800/50 border border-emerald-500/20 p-8 md:p-12 text-center">
            <Heart className="w-16 h-16 text-emerald-400 mx-auto mb-6" />
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              Hay Esperanza
            </h2>
            <p className="text-slate-300 mb-8 max-w-2xl mx-auto">
              Aunque la adicción a estimulantes es severa, la recuperación es posible con el tratamiento adecuado. 
              Muchas personas han logrado reconstruir sus vidas.
            </p>
            
            <div className="flex flex-wrap justify-center gap-4">
              <Link 
                to="/asesoria"
                className="px-8 py-4 bg-emerald-500 hover:bg-emerald-600 text-white font-bold rounded-xl transition-all shadow-lg shadow-emerald-500/25 flex items-center gap-2"
              >
                <Phone className="w-5 h-5" />
                Asesoría Experta
              </Link>
              <a 
                href="tel:1412"
                className="px-8 py-4 bg-slate-800 hover:bg-slate-700 text-white font-semibold rounded-xl transition-all border border-slate-700"
              >
                Fono Drogas: 1412 (24/7)
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Related */}
      <section className="py-16 bg-slate-900/50">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-2xl font-bold text-white mb-6">Contenido Relacionado</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { title: "Alcoholismo", path: "/adicciones/alcohol", icon: "🍺" },
              { title: "Tratamiento Residencial", path: "/centro/residencial", icon: "🏥" },
              { title: "Tratamientos Gratuitos", path: "/centros-rehabilitacion-gratis", icon: "💚" },
              { title: "Cómo Elegir Centro", path: "/como-elegir-centro-rehabilitacion", icon: "📋" }
            ].map((item, index) => (
              <Link 
                key={index}
                to={item.path}
                className="flex items-center gap-3 p-4 bg-slate-800/50 rounded-xl border border-slate-700/50 hover:border-emerald-500/30 transition-all group"
              >
                <span className="text-2xl">{item.icon}</span>
                <span className="text-white group-hover:text-emerald-400 transition-colors">{item.title}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CocainaPage;
